import { useCallback, useMemo, useState } from "react";
import {
  BaseInput,
  Button,
  ButtonSize,
  ButtonVariant,
  IconButton,
  IconButtonSize,
  IconButtonVariant,
  InputSize,
  Show
} from "shared/components";
import {
  Download02Icon,
  FilterIcon,
  PlusSignIcon,
  StarIcon
} from "@hugeicons-pro/core-stroke-rounded";
import {
  CreateLeadDefaults,
  CreateLeadModal
} from "shared/app/LeadsTableView/components/CreateLeadModal";
import { AssigneeFilterPopover } from "shared/components/Assignee";
import { Tabs, type Tab } from "shared/components/BaseTabs";
import { useLocation } from "react-router-dom";
import { ColumnsMenu } from "shared/app/ApplicationsTableView/components/ColumnsMenu";
import { useMedia } from "shared/providers";
import { ColumnVisibilityItem } from "providers/Auth";
import { LEADS_COLUMNS_CONTEXT_LABEL } from "shared/app/LeadsTableView/constants";

type StarredTab = "all" | "starred";

export const LeadsHeader = ({
  refetchLeads,
  search,
  setSearch,
  isCreateLeadModalOpen,
  hasHeaderTitle,
  setIsCreateLeadModalOpen,
  selectedAssigneeIds,
  setSelectedAssigneeIds,
  setShowFilters,
  createLeadDefaults,
  starredFilter,
  setStarredFilter,
  starredCount,
  columnsVisibility,
  onColumnToggle
}: {
  refetchLeads: () => void;
  search: string;
  hasHeaderTitle?: boolean;
  setSearch: (search: string) => void;
  isCreateLeadModalOpen: boolean;
  setIsCreateLeadModalOpen: (isOpen: boolean) => void;
  selectedAssigneeIds: number[] | null;
  setSelectedAssigneeIds: (userIds: number[] | null) => void;
  setShowFilters: (show: boolean) => void;
  createLeadDefaults?: CreateLeadDefaults;
  starredFilter: boolean;
  setStarredFilter: (next: boolean) => void;
  starredCount: number;
  columnsVisibility: ColumnVisibilityItem[];
  onColumnToggle: (id: string, checked: boolean) => void;
}) => {
  const [assigneeFilterOpen, setAssigneeFilterOpen] = useState(false);
  const location = useLocation();
  const { isMobile } = useMedia();

  const shouldShowSideButtons = !location.pathname.includes("/contacts");

  const starredTabs = useMemo<Tab<StarredTab>[]>(
    () => [
      { value: "all", label: "All" },
      {
        value: "starred",
        label: "Starred",
        leftElement: { type: "icon", component: StarIcon },
        rightElement: { type: "counter", count: starredCount }
      }
    ],
    [starredCount]
  );

  const handleStarredTabChange = useCallback(
    (tab: StarredTab) => setStarredFilter(tab === "starred"),
    [setStarredFilter]
  );

  return (
    <header>
      <Show if={hasHeaderTitle}>
        <h1 className="text-2xl font-bold text-content-1000 mb-6">Leads</h1>
      </Show>
      <div className="mb-4">
        <div className="flex flex-wrap gap-3 items-center">
          <div className="max-w-[272px] w-full">
            <BaseInput
              id="leads-search"
              size={InputSize.SM}
              hideOptional
              type="search"
              placeholder="Search leads"
              value={search}
              onChange={setSearch}
              className="text-sm font-medium"
            />
          </div>
          <IconButton
            aria-label="Filters"
            variant={IconButtonVariant.BASE}
            size={IconButtonSize.SM}
            onClick={() => setShowFilters(true)}
            HugeIcon={FilterIcon}
          />
          <ColumnsMenu
            contextLabel={LEADS_COLUMNS_CONTEXT_LABEL}
            contextNoun="table"
            isMobile={isMobile}
            columnsData={columnsVisibility}
            onColumnToggle={onColumnToggle}
          />
          <AssigneeFilterPopover
            open={assigneeFilterOpen}
            onOpenChange={setAssigneeFilterOpen}
            selectedUserIds={selectedAssigneeIds}
            onSelectedUserIdsChange={setSelectedAssigneeIds}
          />
          <Tabs<StarredTab>
            tabs={starredTabs}
            size="sm"
            fullWidthTabs={false}
            active={starredFilter ? "starred" : "all"}
            onChange={handleStarredTabChange}
            tabStyle="outline"
            className="h-10 [&_button]:!h-8 [&_button]:!text-xs [&_button[aria-selected=false]]:text-content-400 [&_button_span.bg-white]:!bg-alpha-100 [&_button_span.bg-white]:!border-transparent [&_button_span.bg-white]:!shadow-none [&_button_svg]:!text-content-200"
          />
          <Show if={shouldShowSideButtons}>
            <div className="ml-auto flex gap-2">
              <Button
                variant={ButtonVariant.BASE}
                size={ButtonSize.SM}
                disabled
                onClick={() => alert("WIP")}
                HugeIcon={Download02Icon}
              >
                Download CSV
              </Button>
              <Button
                variant={ButtonVariant.PRIMARY}
                size={ButtonSize.SM}
                HugeIcon={PlusSignIcon}
                onClick={() => setIsCreateLeadModalOpen(true)}
              >
                Create lead
              </Button>
            </div>
          </Show>
        </div>
      </div>

      <CreateLeadModal
        open={isCreateLeadModalOpen}
        refetchLeads={refetchLeads}
        onClose={() => setIsCreateLeadModalOpen(false)}
        defaults={createLeadDefaults}
      />
    </header>
  );
};
